import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Warning, ArrowRight } from '@phosphor-icons/react';
import API from '@/lib/api';

const PRIO_COLORS = {
  paars: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', dot: 'bg-purple-500' },
  rood:  { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-700',    dot: 'bg-red-500' },
  oranje:{ bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  groen: { bg: 'bg-emerald-50',border: 'border-emerald-300',text: 'text-emerald-700',dot: 'bg-emerald-500' },
  grijs: { bg: 'bg-gray-50',   border: 'border-gray-300',   text: 'text-gray-700',   dot: 'bg-gray-400' },
};

export default function KlantbezoekenWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    API.get('/klantbezoeken/dashboard-widget')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, []);

  if (busy || !data) return null;
  // Skip widget volledig als geen klanten OF geen actie nodig
  const totalen = data.totalen || {};
  const heeftActie = (totalen.geen_aankoop || 0) + (totalen.niet_deze_maand || 0) > 0 || (totalen.paars || 0) > 0;
  if (totalen.totaal === 0) return null;
  if (!heeftActie) return null;

  return (
    <div className="bg-white rounded-lg border-2 border-amber-300 p-4 mb-6 shadow-sm" data-testid="klantbezoeken-widget">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin size={22} weight="duotone" className="text-amber-600" />
          <div>
            <h3 className="font-bold text-gray-900">Klantbezoeken — actie nodig</h3>
            <div className="text-xs text-gray-500">
              {totalen.geen_aankoop || 0} klanten zonder recente aankoop ·
              {' '}{totalen.niet_deze_maand || 0} nog niet bezocht deze maand
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/klantbezoeken')}
          className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1"
          data-testid="kb-widget-open-btn"
        >
          Plan bezoeken <ArrowRight size={14} weight="bold" />
        </button>
      </div>

      {/* Mini-totalen — mockup-stijl */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="rounded-lg p-2 bg-gray-50 border-2 border-gray-200">
          <div className="text-lg font-bold text-gray-900">{totalen.totaal || 0}</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Klanten totaal</div>
        </div>
        <div className="rounded-lg p-2 bg-red-50 border-2 border-red-300">
          <div className="text-lg font-bold text-red-800">{totalen.geen_aankoop || 0}</div>
          <div className="text-[10px] text-red-700 uppercase tracking-wide">Geen aankoop &gt;{data.settings?.drempel_aankoop_dagen || 60}d</div>
        </div>
        <div className="rounded-lg p-2 bg-amber-50 border-2 border-amber-300">
          <div className="text-lg font-bold text-amber-800">{totalen.niet_deze_maand || 0}</div>
          <div className="text-[10px] text-amber-700 uppercase tracking-wide">Niet bezocht deze maand</div>
        </div>
        <div className="rounded-lg p-2 bg-emerald-50 border-2 border-emerald-300">
          <div className="text-lg font-bold text-emerald-800">{totalen.wel_deze_maand || 0}</div>
          <div className="text-[10px] text-emerald-700 uppercase tracking-wide">Wel bezocht deze maand</div>
        </div>
      </div>

      {/* Top overdue */}
      {data.top_overdue && data.top_overdue.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-600 mb-1.5">Hoogste prioriteit (top 5)</div>
          <div className="space-y-1">
            {data.top_overdue.slice(0, 5).map(o => {
              const style = PRIO_COLORS[o.kleur] || PRIO_COLORS.grijs;
              return (
                <div
                  key={o.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-gray-50 cursor-pointer`}
                  onClick={() => navigate('/klantbezoeken')}
                  data-testid={`kb-widget-overdue-${o.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                  <span className="flex-1 font-medium truncate">{o.naam}</span>
                  {o.verkoper && <span className="text-xs text-gray-500 hidden sm:inline">{o.verkoper}</span>}
                  <span className={`text-xs font-semibold ${style.text}`}>
                    {o.label || (o.dagen === null ? 'Nooit' : `${o.dagen} dgn`)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
